import {CustomSettings} from '@domain/custom-settings/custom-settings';
import {NameGenerator} from '@domain/custom-settings/name-generator';
import {AssertionUtils} from '@vdw/angular-component-library';
import {FreeZoneSettings} from './free-zone-settings';
import {LoomScheduleSettings} from './loom-schedule-settings';
import {PathLabelSettings} from './path-label-settings';
import {RestZoneSettings} from './rest-zone-settings';

export class ProductionOrderCustomSettings extends CustomSettings {
  private _maximumLossInPercent: number;
  private _maxDifferenceInPathLengthInMillimeters: number;
  private _allowExceedingRequestedAmountOfOrderLineItems: boolean;
  private _allowPreFilterOnQualitiesAndColoredYarnSets: boolean;
  private _addProductionOrderToPlanningBoard: boolean;
  private _restZone: RestZoneSettings;
  private _freeZone: FreeZoneSettings;
  private _pathLabel: PathLabelSettings;
  private _loomSchedule: LoomScheduleSettings;
  private _epNameGenerator: NameGenerator;

  public constructor(
    id: number,
    nameGenerator: NameGenerator,
    maximumLossInPercent: number,
    maxDifferenceInPathLengthInMillimeters: number,
    allowExceedingRequestedAmountOfOrderLineItems: boolean,
    allowPreFilterOnQualitiesAndColoredYarnSets: boolean,
    addProductionOrderToPlanningBoard: boolean,
    restZone: RestZoneSettings,
    freeZone: FreeZoneSettings,
    pathLabel: PathLabelSettings,
    loomSchedule: LoomScheduleSettings,
    epNameGenerator: NameGenerator
  ) {
    super(id, nameGenerator);
    this._maximumLossInPercent = maximumLossInPercent;
    this._maxDifferenceInPathLengthInMillimeters = maxDifferenceInPathLengthInMillimeters;
    this._allowExceedingRequestedAmountOfOrderLineItems = allowExceedingRequestedAmountOfOrderLineItems;
    this._allowPreFilterOnQualitiesAndColoredYarnSets = allowPreFilterOnQualitiesAndColoredYarnSets;
    this._addProductionOrderToPlanningBoard = addProductionOrderToPlanningBoard;
    this._restZone = restZone;
    this._freeZone = freeZone;
    this._pathLabel = pathLabel;
    this._loomSchedule = loomSchedule;
    this._epNameGenerator = epNameGenerator;
  }

  public get maximumLossInPercent(): number {
    return this._maximumLossInPercent;
  }

  public set maximumLossInPercent(value: number) {
    this._maximumLossInPercent = value;
  }

  public get maxDifferenceInPathLengthInMillimeters(): number {
    return this._maxDifferenceInPathLengthInMillimeters;
  }

  public set maxDifferenceInPathLengthInMillimeters(value: number) {
    this._maxDifferenceInPathLengthInMillimeters = value;
  }

  public get allowExceedingRequestedAmountOfOrderLineItems(): boolean {
    return this._allowExceedingRequestedAmountOfOrderLineItems;
  }

  public set allowExceedingRequestedAmountOfOrderLineItems(value: boolean) {
    this._allowExceedingRequestedAmountOfOrderLineItems = value;
  }

  public get allowPreFilterOnQualitiesAndColoredYarnSets(): boolean {
    return this._allowPreFilterOnQualitiesAndColoredYarnSets;
  }

  public set allowPreFilterOnQualitiesAndColoredYarnSets(value: boolean) {
    this._allowPreFilterOnQualitiesAndColoredYarnSets = value;
  }

  public get addProductionOrderToPlanningBoard(): boolean {
    return this._addProductionOrderToPlanningBoard;
  }

  public set addProductionOrderToPlanningBoard(value: boolean) {
    this._addProductionOrderToPlanningBoard = value;
  }

  public get restZone(): RestZoneSettings {
    return this._restZone;
  }

  public set restZone(value: RestZoneSettings) {
    this._restZone = value;
  }

  public get freeZone(): FreeZoneSettings {
    return this._freeZone;
  }

  public set freeZone(value: FreeZoneSettings) {
    this._freeZone = value;
  }

  public get pathLabel(): PathLabelSettings {
    return this._pathLabel;
  }

  public set pathLabel(value: PathLabelSettings) {
    this._pathLabel = value;
  }

  public get loomSchedule(): LoomScheduleSettings {
    return this._loomSchedule;
  }

  public set loomSchedule(value: LoomScheduleSettings) {
    this._loomSchedule = value;
  }

  public get epNameGenerator(): NameGenerator {
    return this._epNameGenerator;
  }

  public set epNameGenerator(value: NameGenerator) {
    this._epNameGenerator = value;
  }

  public toJSON(): JSON {
    return {
      ...(super.toJSON() as any),
      maximumLossInPercent: this._maximumLossInPercent,
      maxDifferenceInPathLengthInMillimeters: this._maxDifferenceInPathLengthInMillimeters,
      allowExceedingRequestedAmountOfOrderLineItems: this._allowExceedingRequestedAmountOfOrderLineItems,
      allowPreFilterOnQualitiesAndColoredYarnSets: this._allowPreFilterOnQualitiesAndColoredYarnSets,
      addProductionOrderToPlanningBoard: this._addProductionOrderToPlanningBoard,
      restZone: this._restZone.toJSON(),
      freeZone: this._freeZone.toJSON(),
      pathLabel: this._pathLabel.toJSON(),
      loomSchedule: this._loomSchedule.toJSON(),
      epNameGenerator: this._epNameGenerator?.toJSON() ?? null
    };
  }

  public static fromJSON(json: any): ProductionOrderCustomSettings {
    return new ProductionOrderCustomSettings(
      json.id,
      NameGenerator.fromJSON(json.nameGenerator),
      json.maximumLossInPercent,
      json.maxDifferenceInPathLengthInMillimeters,
      json.allowExceedingRequestedAmountOfOrderLineItems,
      json.allowPreFilterOnQualitiesAndColoredYarnSets,
      json.addProductionOrderToPlanningBoard,
      RestZoneSettings.fromJSON(json.restZone),
      FreeZoneSettings.fromJSON(json.freeZone),
      PathLabelSettings.fromJSON(json.pathLabel),
      LoomScheduleSettings.fromJSON(json.loomSchedule),
      !AssertionUtils.isNullOrUndefined(json.epNameGenerator) ? NameGenerator.fromJSON(json.epNameGenerator) : null
    );
  }
}
